import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import CustomRangeSelection from "./CustomRangeSelection";
import { observer } from "mobx-react";
import { dataSearchState } from "../state";
import { Utils } from "Utils/utils";

const getTriggerRect = (triggerRef) => {
  const el = triggerRef?.current;
  if (!el) return null;
  return el.getBoundingClientRect();
};

export const ContactRangeSelection = observer((props = {}) => {
  const {
    triggerRef = { current: null },
    bulkPurchaseEnabled = false,
    allTableContactChecked = false,
    pagedContacts = 0,
    topPurchaseLimit = 0,
    customBulkPurchaseLimit = 1000,
    isCustomRangeSelector = false,
    setCustomRangeSelector = () => {},
    setShowRangeSelector = () => {},
    markSelectedContact = () => {}
  } = props || {};

  const [position, setPosition] = useState({ top: 0, left: 0 });
  const positionUpdateRef = useRef(null);

  const [range, setRange] = useState("");
  const [err, setErr] = useState("");

  const selectedCallback = (id = "", limit = 0) => {
    let checkedValue = allTableContactChecked
      ? dataSearchState?.rangeSelected?.type === id
        ? false
        : true
      : limit >= pagedContacts
      ? true
      : false;
    setShowRangeSelector(false);
    markSelectedContact(checkedValue, id, limit);
  };

  const handleSelection = (id = "") => {
    if (id === "custom") {
      setCustomRangeSelector(true);
    } else {
      let limit = id === "all" ? pagedContacts : topPurchaseLimit;
      selectedCallback(id, limit);
    }
  };

  const dropdownList = [
    {
      id: "all",
      keyValue: "limitPerPage",
      name: `Select ${Utils.formatNumberToStringInUSFormat(pagedContacts)} contact${
        pagedContacts > 1 ? "s" : ""
      } in this page`
    },
    {
      id: "bulk",
      keyValue: "bulkLimit",
      name: `Select top ${Utils.formatNumberToStringInUSFormat(topPurchaseLimit)} contacts`
    },
    {
      id: "custom",
      keyValue: "customRange",
      name: "Custom Selection"
    }
  ];

  const updatePosition = () => {
    const rect = getTriggerRect(triggerRef);
    if (rect) {
      setPosition({ top: rect.bottom + 2, left: rect.left });
    }
  };

  useEffect(() => {
    updatePosition();
    const initialRaf = requestAnimationFrame(updatePosition);
    const onScrollOrResize = () => {
      if (positionUpdateRef.current) cancelAnimationFrame(positionUpdateRef.current);
      positionUpdateRef.current = requestAnimationFrame(updatePosition);
    };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      cancelAnimationFrame(initialRaf);
      setRange("");
      setErr("");
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      if (positionUpdateRef.current) cancelAnimationFrame(positionUpdateRef.current);
    };
  }, [triggerRef]);

  const dropdownContent = (
    <div
      className="contactRangeSelection contactRangeSelection--portal"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        zIndex: 10000
      }}
    >
      <div className="rangeSelector">
        {!isCustomRangeSelector ? (
          dropdownList.map(item => {
            const { id = "", name = "", keyValue = "" } = item || {};
            return (
              <div
                key={keyValue}
                id={id}
                onClick={() => handleSelection(id)}
                className={`itemList ${
                  dataSearchState?.rangeSelected?.type === id ? "active" : ""
                }`}
              >
                {name}
              </div>
            );
          })
        ) : (
          <CustomRangeSelection
            range={range}
            setRange={setRange}
            err={err}
            setErr={setErr}
            customAction={selectedCallback}
            setCustomRangeSelector={setCustomRangeSelector}
            customBulkPurchaseLimit={customBulkPurchaseLimit}
          />
        )}
      </div>
      <div
        className="bgOverlay"
        onClick={() => {
          setShowRangeSelector(false);
          isCustomRangeSelector && setCustomRangeSelector(false);
        }}
      />
    </div>
  );

  return createPortal(dropdownContent, document.body);
});

export default ContactRangeSelection;
